import React, { useState, useEffect } from "react";
import { getQuotes, updateQuote, getMessages, deleteQuote, deleteMessage } from "../../api";
import { useToast } from "../../hooks/use-toast";
import { RefreshCw, Building2, Mail, Trash2 } from "lucide-react";

const STATUS = ["nouveau", "en_cours", "gagne", "perdu"];
const statusColor = {
  nouveau: "bg-blue-100 text-blue-700",
  en_cours: "bg-amber-100 text-amber-700",
  gagne: "bg-emerald-100 text-emerald-700",
  perdu: "bg-neutral-200 text-neutral-600",
};

export const QuotesPanel = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const load = async () => {
    setLoading(true);
    try {
      const res = await getQuotes();
      setQuotes(res.data);
    } catch (e) {
      toast({ title: "Erreur", description: "Chargement impossible", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    await updateQuote(id, status);
    setQuotes((q) => q.map((x) => (x.id === id ? { ...x, status } : x)));
  };

  const remove = async (id) => {
    await deleteQuote(id);
    setQuotes((q) => q.filter((x) => x.id !== id));
    toast({ title: "Devis supprimé" });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-heading font-bold text-xl text-neutral-900">Demandes de devis</h2>
        <button onClick={load} className="flex items-center gap-2 text-sm text-neutral-600 hover:text-brand">
          <RefreshCw size={15} /> Actualiser
        </button>
      </div>
      {loading ? (
        <p className="text-neutral-500">Chargement...</p>
      ) : quotes.length === 0 ? (
        <p className="text-neutral-500">Aucune demande pour le moment.</p>
      ) : (
        <div className="space-y-3">
          {quotes.map((q) => (
            <div key={q.id} className="bg-white border border-neutral-200 rounded-xl p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <Building2 size={16} className="text-brand" />
                    <span className="font-semibold text-neutral-900">{q.societe}</span>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded ${statusColor[q.status] || ""}`}>{q.status}</span>
                  </div>
                  <p className="text-sm text-neutral-600 mt-1">{q.contact} · {q.email} · {q.telephone}</p>
                  <p className="text-sm text-neutral-700 mt-2">
                    <b>{q.prestation}</b> · {q.frequence || "—"} · {q.surface || "?"} m² · {q.localisation}
                    {q.surface_pelouse && <span data-testid={`quote-pelouse-${q.id}`}> · pelouse {q.surface_pelouse} m²</span>}
                    {q.lineaire_haies && <span> · haies {q.lineaire_haies} m</span>}
                    {q.heures_estimees && <span data-testid={`quote-heures-${q.id}`}> · {q.heures_estimees} h estimées</span>}
                  </p>
                  {q.details && <p className="text-sm text-neutral-500 mt-1">{q.details}</p>}
                  {q.dolibarr_socid && (
                    <p className="text-xs text-emerald-600 mt-2">✓ Dolibarr: tiers #{q.dolibarr_socid}{q.dolibarr_proposal_id ? `, devis #${q.dolibarr_proposal_id}` : ""}</p>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <select
                    value={q.status}
                    onChange={(e) => setStatus(q.id, e.target.value)}
                    className="border border-neutral-300 rounded-md px-3 py-1.5 text-sm"
                  >
                    {STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <button onClick={() => remove(q.id)} className="text-neutral-400 hover:text-red-500 p-1.5" title="Supprimer">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const MessagesPanel = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getMessages();
      setMessages(res.data);
    } finally {
      setLoading(false);
    }
  };
   
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    await deleteMessage(id);
    setMessages((m) => m.filter((x) => x.id !== id));
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-heading font-bold text-xl text-neutral-900">Messages de contact</h2>
        <button onClick={load} className="flex items-center gap-2 text-sm text-neutral-600 hover:text-brand">
          <RefreshCw size={15} /> Actualiser
        </button>
      </div>
      {loading ? (
        <p className="text-neutral-500">Chargement...</p>
      ) : messages.length === 0 ? (
        <p className="text-neutral-500">Aucun message.</p>
      ) : (
        <div className="space-y-3">
          {messages.map((m) => (
            <div key={m.id} className="bg-white border border-neutral-200 rounded-xl p-5">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Mail size={16} className="text-brand" />
                  <span className="font-semibold text-neutral-900">{m.nom}</span>
                  <span className="text-sm text-neutral-500">{m.email}</span>
                </div>
                <button onClick={() => remove(m.id)} className="text-neutral-400 hover:text-red-500 p-1.5" title="Supprimer">
                  <Trash2 size={16} />
                </button>
              </div>
              <p className="text-sm text-neutral-700 mt-2">{m.message}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
